import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  Text,
  HStack,
  VStack,
  Button,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
  Icon,
  Tooltip,
  Progress,
} from '@chakra-ui/react';
import { FiRefreshCw, FiUsers, FiLayers, FiClock } from 'react-icons/fi';

const API_BASE = '/api/v1';

function TenantOverview() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  const fetchTenants = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/tenants/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setTenants(data.tenants || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const totalRecords = tenants.reduce(
    (sum, t) => sum + (t.contacts || 0) + (t.configurations || 0) + (t.webhooks || 0) + (t.sync_runs || 0) + (t.audit_logs || 0),
    0
  );

  const activeTenants = tenants.filter(
    t => (t.contacts || 0) + (t.configurations || 0) + (t.webhooks || 0) + (t.sync_runs || 0) > 0
  ).length;

  if (loading) {
    return (
      <Box textAlign="center" py={20}>
        <Spinner size="xl" color="orange.500" />
        <Text mt={4} color="gray.500">Loading tenant data…</Text>
      </Box>
    );
  }

  return (
    <Box>
      <HStack justify="space-between" mb={8}>
        <Heading size="lg">Tenant Overview</Heading>
        <Button leftIcon={<FiRefreshCw />} onClick={fetchTenants} colorScheme="orange" variant="outline" size="sm">
          Refresh
        </Button>
      </HStack>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={8}>
        <Stat bg="white" p={6} borderRadius="xl" boxShadow="sm">
          <StatLabel color="gray.500"><HStack><Icon as={FiLayers} /><Text>Total Tenants</Text></HStack></StatLabel>
          <StatNumber fontSize="3xl">{tenants.length}</StatNumber>
        </Stat>
        <Stat bg="white" p={6} borderRadius="xl" boxShadow="sm">
          <StatLabel color="gray.500"><HStack><Icon as={FiUsers} /><Text>Active Tenants</Text></HStack></StatLabel>
          <StatNumber fontSize="3xl">{activeTenants}</StatNumber>
        </Stat>
        <Stat bg="white" p={6} borderRadius="xl" boxShadow="sm">
          <StatLabel color="gray.500"><HStack><Icon as={FiClock} /><Text>Total Records</Text></HStack></StatLabel>
          <StatNumber fontSize="3xl">{totalRecords.toLocaleString()}</StatNumber>
        </Stat>
      </SimpleGrid>

      <Box bg="white" borderRadius="xl" boxShadow="sm" overflow="hidden">
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th>Tenant</Th>
              <Th isNumeric>Contacts</Th>
              <Th isNumeric>Configs</Th>
              <Th isNumeric>Webhooks</Th>
              <Th isNumeric>Sync Runs</Th>
              <Th isNumeric>Audit Logs</Th>
              <Th>First Seen</Th>
              <Th>Last Active</Th>
              <Th>Share</Th>
            </Tr>
          </Thead>
          <Tbody>
            {tenants.map((t, idx) => {
              const recordCount = (t.contacts || 0) + (t.configurations || 0) + (t.webhooks || 0) + (t.sync_runs || 0) + (t.audit_logs || 0);
              const share = totalRecords > 0 ? ((recordCount / totalRecords) * 100) : 0;
              return (
                <Tr key={idx} _hover={{ bg: 'gray.50' }}>
                  <Td>
                    <VStack align="start" spacing={0}>
                      <Badge colorScheme={t.tenant_id ? 'orange' : 'gray'} fontSize="sm">
                        {t.label}
                      </Badge>
                    </VStack>
                  </Td>
                  <Td isNumeric fontWeight="semibold">{t.contacts || 0}</Td>
                  <Td isNumeric>{t.configurations || 0}</Td>
                  <Td isNumeric>{t.webhooks || 0}</Td>
                  <Td isNumeric>{t.sync_runs || 0}</Td>
                  <Td isNumeric>{t.audit_logs || 0}</Td>
                  <Td>
                    <Tooltip label={t.first_seen || 'N/A'}>
                      <Text fontSize="sm" color="gray.600">
                        {t.first_seen ? new Date(t.first_seen).toLocaleDateString() : '—'}
                      </Text>
                    </Tooltip>
                  </Td>
                  <Td>
                    <Tooltip label={t.last_active || 'N/A'}>
                      <Text fontSize="sm" color="gray.600">
                        {t.last_active ? new Date(t.last_active).toLocaleDateString() : '—'}
                      </Text>
                    </Tooltip>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <Progress
                        value={share}
                        size="sm"
                        colorScheme="orange"
                        borderRadius="full"
                        width="80px"
                      />
                      <Text fontSize="xs" color="gray.500">{share.toFixed(1)}%</Text>
                    </HStack>
                  </Td>
                </Tr>
              );
            })}
            {tenants.length === 0 && (
              <Tr>
                <Td colSpan={9} textAlign="center" py={8}>
                  <Text color="gray.400">No tenant data found</Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

export default TenantOverview;
